/**
 * Thumbs up or down with a reason code (PRD §4.5), and undo. A vote needs a reason, chosen from a
 * fixed list: free text would be notes about a person, which Waterlily does not keep. Reasons marked
 * "remove from list" take the contact off the list on the next scoring pass; undo brings them back.
 */
import { useState } from 'react';
import { Button, Select, SelectContent, SelectItem, SelectTrigger, SelectValue, Spinner } from '@design-system';
import { ThumbsDown, ThumbsUp } from 'lucide-react';
import type { Prospecting } from '@client/api/types';
import { feedbackReasons, formatDate } from '../lib/labels';

interface FeedbackControlProps {
  /** The CRM the book is from, as shown to the advisor; one reason names it. */
  crmName: string;
  latest: Prospecting.ProspectDetail['latestFeedback'];
  isSaving: boolean;
  error: string | null;
  onVote: (vote: 'up' | 'down', reasonCode: string) => void;
  onUndo: () => void;
}

export function FeedbackControl({ crmName, latest, isSaving, error, onVote, onUndo }: FeedbackControlProps) {
  const [choosing, setChoosing] = useState<'up' | 'down' | null>(null);
  const reasons = feedbackReasons(crmName);
  const current = latest && latest.vote !== 'cleared' ? latest : null;

  if (current && !choosing) {
    const reason = reasons[current.vote as 'up' | 'down'].find(entry => entry.code === current.reasonCode);
    return (
      <div className="flex flex-wrap items-center gap-2 text-sm text-gray-700">
        {current.vote === 'up' ? <ThumbsUp className="size-4" /> : <ThumbsDown className="size-4" />}
        <span>
          {reason?.label ?? 'Voted'} · {formatDate(current.at)}
        </span>
        <Button variant="link" size="sm" disabled={isSaving} onClick={onUndo}>
          Undo
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <span className="text-sm text-gray-700">Is this a good prospect?</span>
        <Button
          variant={choosing === 'up' ? 'default' : 'outline'}
          size="icon-sm"
          aria-label="Good prospect"
          disabled={isSaving}
          onClick={() => setChoosing('up')}
        >
          <ThumbsUp />
        </Button>
        <Button
          variant={choosing === 'down' ? 'default' : 'outline'}
          size="icon-sm"
          aria-label="Not a good prospect"
          disabled={isSaving}
          onClick={() => setChoosing('down')}
        >
          <ThumbsDown />
        </Button>
        {isSaving && (
          <span role="status" className="flex items-center gap-1.5 text-sm text-gray-600">
            <Spinner aria-hidden="true" />
            Saving your answer…
          </span>
        )}
      </div>
      {choosing && (
        <Select
          onValueChange={reasonCode => {
            onVote(choosing, reasonCode);
            setChoosing(null);
          }}
          disabled={isSaving}
        >
          <SelectTrigger className="w-full" aria-label="Reason">
            <SelectValue placeholder="Choose a reason" />
          </SelectTrigger>
          <SelectContent>
            {reasons[choosing].map(reason => (
              <SelectItem key={reason.code} value={reason.code}>
                {reason.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
      {error && <span className="text-sm text-destructive">{error}</span>}
    </div>
  );
}
